import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Input, Select, Row, Space, DatePicker } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import moment from 'moment';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  clearCustomers,
  clearProductIds,
  clearStylesForJobchart,
  getCustomers,
  getProductIds,
  getStylesForJobchart,
  getColorIds,
  getFabricStocks,
  clearColorIds,
  clearFabricStocks,
  getSavedJobcharts,
  clearSavedJobcharts,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import ProductForm from './ProductForm';
import SavedJobChartsList from './SavedJobChartsList';
import SummaryModal from './Summary';

const AntInput = GetField(Input);
const AntTextArea = GetField(Input.TextArea);
const AntSelect = GetField(Select);
const AntDatePicker = GetField(DatePicker);
const { Option } = Select;

const CreateJobchart = props => {
  const { classes } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmLoadingSave, setConfirmLoadingSave] = useState(false);
  const [viewSavedJobChartsModal, setViewSavedJobChartsModal] = useState(false);
  const [summary, setSummary] = useState(false);

  const filesState = useSelector(state => state.jobchartFiles);
  const fabricStocks = useSelector(state => state.fabricStocks);
  const accessoryStocks = useSelector(state => state.accessoryStocks);

  const viewSummary = () => setSummary(true);

  const { handleSubmit, control, getValues, setValue, watch, unregister } =
    useForm({
      defaultValues: {
        products: [
          {
            productId: null,
            isAll: false,
            styles: [
              {
                fabrics: [{ fabricId: null }],
                accessories: [{ accessoryId: null }],
              },
            ],
          },
        ],
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  });

  const customers = useSelector(state => state.customers);
  const savedJobcharts = useSelector(state => state.savedJobcharts);

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getColorIds());
    dispatch(getFabricStocks());
    return () => {
      dispatch(clearCustomers());
      dispatch(clearProductIds());
      dispatch(clearColorIds());
      dispatch(clearFabricStocks());
      dispatch(clearSavedJobcharts());
    };
  }, []);

  useEffect(() => {
    if (viewSavedJobChartsModal) {
      dispatch(getSavedJobcharts());
    }
  }, [viewSavedJobChartsModal]);

  const hideModal = useCallback(() => {
    setViewSavedJobChartsModal(false);
    dispatch(clearSavedJobcharts());
  }, []);

  const calculateStocks = () => {
    const products = getValues('products');
    const _products = JSON.parse(JSON.stringify(products));

    _products.map(product => {
      (product.styles || []).map(style => {
        style.quantity = +style.quantity || 0;
        style.additional = +style.additional || 0;

        style.totalQuantity = style.quantity + style.additional;

        (style.fabrics || []).map(fabric => {
          const fabricId = fabric.fabricId || '';
          const productId = product.productId || '';
          const baseColorId = fabric.baseColorId || '';
          const printColorId = fabric.printColorId || '';

          fabric.consumption = +fabric.consumption || 0;
          fabric.additionalPercentage = +fabric.additionalPercentage || 0;

          fabric.quantity = +(fabric.consumption * style.totalQuantity).toFixed(
            2
          );

          fabric.totalQuantity = +(
            fabric.quantity *
            (1 + +fabric.additionalPercentage / 100)
          ).toFixed(2);

          const stock =
            fabricStocks[
              `P${productId}F${fabricId}B${baseColorId}P${printColorId}`
            ];

          fabric.stock = stock ? +stock.stock : 0;

          fabric.toOrder =
            fabric.stock > fabric.totalQuantity
              ? 0
              : +(fabric.totalQuantity - fabric.stock).toFixed(2);
        });

        (style.accessories || []).map(accessory => {
          const accessoryId = accessory.accessoryId || '';
          const productId = product.productId || '';

          accessory.consumption = +accessory.consumption || 0;
          accessory.additionalPercentage = +accessory.additionalPercentage || 0;

          accessory.quantity = +(
            accessory.consumption * style.totalQuantity
          ).toFixed(2);

          accessory.totalQuantity = +(
            accessory.quantity *
            (1 + +accessory.additionalPercentage / 100)
          ).toFixed(2);

          const stock = accessoryStocks[`P${productId}A${accessoryId}`];

          accessory.stock = stock ? +stock.stock : 0;

          accessory.toOrder =
            accessory.stock > accessory.totalQuantity
              ? 0
              : +(accessory.totalQuantity - accessory.stock).toFixed(2);
        });
      });
    });

    setValue('products', _products);
  };

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      const formData = new FormData();

      const { productIndexes } = filesState;

      formData.append(
        'data',
        JSON.stringify({
          ...formValues,
          shipDate: formValues.shipDate
            ? moment(formValues.shipDate).toISOString()
            : null,
          jobDate: formValues.jobDate
            ? moment(formValues.jobDate).toISOString()
            : null,
          productIndexes,
        })
      );

      let index = 0;

      for (const key of Object.keys(filesState.products)) {
        if (filesState.products[key] && filesState.products[key].files.length) {
          for (const file of filesState.products[key].files) {
            formData.append(`files[${index}]`, file);
            index += 1;
          }
        }
      }

      await Api.post('/jobcharts', formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });

      successNotification('Jobchart created');
      history.push('/jobcharts/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  const productAppend = useCallback(() => {
    append({ isAll: false, styles: [{ fabrics: [{}], accessories: [{}] }] });
  }, [append]);

  const saveJobchart = useCallback(async () => {
    try {
      setConfirmLoadingSave(true);
      const formValues = getValues();

      await Api.post('/jobcharts/save', formValues);

      successNotification('Jobchart saved');

      history.push('/jobcharts/list');
    } catch (error) {
      setConfirmLoadingSave(false);
      console.error(error);
      errorNotification(error);
    }
  }, []);

  const getSavedJobchart = useCallback(async (customerId, bpoNo) => {
    try {
      const { data } = await Api.get('/jobcharts/saved', { customerId, bpoNo });

      setValue('id', data.id);

      if (data.products) {
        setValue('products', data.products);
      } else {
        setValue('products', [
          {
            productId: null,
            styles: [
              {
                fabrics: [{ fabricId: null }],
                accessories: [{ accessoryId: null }],
              },
            ],
          },
        ]);
      }
      if (data.shipDate) {
        setValue('shipDate', moment(data.shipDate));
      } else {
        setValue('shipDate', null);
      }
      if (data.jobDate) {
        setValue('jobDate', moment(data.jobDate));
      } else {
        setValue('jobDate', null);
      }
    } catch (error) {
      console.error('no jobchart found');
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Jobchart"
        moduleLink="/jobcharts/list"
        subModuleName="Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                  onChange={e => {
                    field.onChange(e);

                    if (!e) {
                      dispatch(clearStylesForJobchart());
                      dispatch(clearProductIds());
                    } else {
                      dispatch(getProductIds(e));
                      dispatch(getStylesForJobchart(e));
                    }

                    const bpoNo = getValues('bpoNo');
                    if (bpoNo && e) {
                      getSavedJobchart(e, bpoNo);
                    }
                  }}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  label="BPO #"
                  placeholder="BPO #"
                  {...field}
                  onChange={e => {
                    field.onChange(e);

                    if (e.target.value) {
                      const customerId = getValues('customerId');
                      if (customerId) {
                        getSavedJobchart(
                          customerId || undefined,
                          e.target.value
                        );
                      }
                    }
                  }}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="shipDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  allowClear
                  label="Ship Date"
                  placeholder="Ship Date"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="jobDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  allowClear
                  label="BPO Date"
                  placeholder="BPO Date"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Button
              type="primary"
              className={classes.savedJobChartsButton}
              onClick={() => setViewSavedJobChartsModal(true)}
            >
              Saved Job Charts
            </Button>
          </RightGrid>
        </Row>
        <Row style={{ padding: 30 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {fields.map((item, index) => (
              <ProductForm
                key={item.id}
                productItem={item}
                productIndex={index}
                productRemove={remove}
                control={control}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                unregister={unregister}
              />
            ))}
          </Space>
        </Row>

        <Row className={classes.buttonContainer}>
          <Button type="primary" htmlType="button" onClick={productAppend}>
            <PlusOutlined /> Product
          </Button>
          &nbsp; &nbsp;
          <Button
            type="primary"
            loading={confirmLoadingSave}
            htmlType="button"
            onClick={saveJobchart}
          >
            Save
          </Button>
          &nbsp; &nbsp;
          <Button
            type="primary"
            htmlType="button"
            onClick={() => calculateStocks()}
          >
            Calculate
          </Button>
          &nbsp; &nbsp;
          <Button type="primary" htmlType="button" onClick={viewSummary}>
            Summary
          </Button>
          &nbsp; &nbsp;
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
      {viewSavedJobChartsModal ? (
        <SavedJobChartsList
          savedJobcharts={savedJobcharts}
          viewSavedJobChartsModal={viewSavedJobChartsModal}
          hideModal={hideModal}
        />
      ) : null}
      {summary && <SummaryModal getValues={getValues} setModal={setSummary} />}
    </>
  );
};

CreateJobchart.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreateJobchart);
